import { HubFeature } from '@ats/api/cm';
import { LocDefs, LocMap, LocPortal, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Profile|My Information|Password Reset';
const sharedProps = {
	portal: 'CP' as LocPortal,
	feature: HubFeature.CandidateAccessLogin,
};

export type PasswordResetFormLoc =
	| 'CPPasswordResetFormLabelOldPassword'
	| 'CPPasswordResetFormButtonShowPassword'
	| 'CPPasswordResetFormButtonHidePassword'
	| 'CPPasswordResetFormErrorOldPasswordRequired'
	| 'CPPasswordResetFormLabelPassword'
	| 'CPPasswordResetFormErrorPasswordRequired'
	| 'CPPasswordResetFormErrorPasswordMinLength'
	| 'CPPasswordResetFormErrorPasswordMaxLength'
	| 'CPPasswordResetFormErrorPasswordMatch'
	| 'CPPasswordResetFormLabelPasswordConfirm'
	| 'CPPasswordResetFormErrorPasswordConfirmRequired'
	| 'CPPasswordResetFormButtonResetPassword'
	| 'CPPasswordResetFormNotificationProblem'
	| 'CPPasswordResetFormNotificationPasswordUpdated';

export const cpPasswordResetFormLocs: LocDefs<PasswordResetFormLoc> = {
	CPPasswordResetFormLabelOldPassword: {
		defaultValue: 'Old Password',
		description: 'Label for the old password field.',
		displayPath: `${dpRoot}|Old Password Label`,
		displayType: 'Label',
		legacyName: 'CandidateAccess.Login/Profile.Information.FieldLabel.OldPassword',
		...sharedProps,
	},
	CPPasswordResetFormButtonShowPassword: {
		defaultValue: 'Show password',
		description: 'Tooltip and aria-label for the show password button.',
		displayPath: `${dpRoot}|Show Password Button`,
		displayType: 'Button',
		legacyName: 'CandidateAccess.Login/Profile.Information.Button.ShowPassword',
		...sharedProps,
	},
	CPPasswordResetFormButtonHidePassword: {
		defaultValue: 'Hide Password',
		description: 'Tooltip and aria-label for the hide password button.',
		displayPath: `${dpRoot}|Hide Password Button`,
		displayType: 'Button',
		legacyName: 'CandidateAccess.Login/Profile.Information.Button.HidePassword',
		...sharedProps,
	},
	CPPasswordResetFormErrorOldPasswordRequired: {
		defaultValue: 'Old password is required',
		description: 'Error message when old password is not provided.',
		displayPath: `${dpRoot}|Old Password Required Error`,
		displayType: 'Error',
		legacyName: 'CandidateAccess.Login/Profile.Information.FieldError.OldPassword',
		...sharedProps,
	},
	CPPasswordResetFormLabelPassword: {
		defaultValue: 'Password',
		description: 'Label for the new password field.',
		displayPath: `${dpRoot}|Password Label`,
		displayType: 'Label',
		legacyName: 'CandidateAccess.Login/Profile.Information.FieldLabel.Password',
		...sharedProps,
	},
	CPPasswordResetFormErrorPasswordRequired: {
		defaultValue: 'Password is required',
		description: 'Error message when password is not provided.',
		displayPath: `${dpRoot}|Password Required Error`,
		displayType: 'Error',
		legacyName: 'CandidateAccess.Login/Profile.Information.FieldError.PasswordRequired',
		...sharedProps,
	},
	CPPasswordResetFormErrorPasswordMinLength: {
		defaultValue: 'Password must contain at least 12 characters',
		description: 'Error message when password does not meet minimum length.',
		displayPath: `${dpRoot}|Password Min Length Error`,
		displayType: 'Error',
		legacyName: 'CandidateAccess.Login/Profile.Information.FieldError.PasswordMinLength',
		...sharedProps,
	},
	CPPasswordResetFormErrorPasswordMaxLength: {
		defaultValue: 'Password cannot contain more than 50 characters',
		description: 'Error message when password exceeds maximum length.',
		displayPath: `${dpRoot}|Password Max Length Error`,
		displayType: 'Error',
		legacyName: 'CandidateAccess.Login/Profile.Information.FieldError.PasswordMaxLength',
		...sharedProps,
	},
	CPPasswordResetFormErrorPasswordMatch: {
		defaultValue: 'Passwords do not match',
		description: 'Error message when password and confirmation do not match.',
		displayPath: `${dpRoot}|Password Match Error`,
		displayType: 'Error',
		legacyName: 'CandidateAccess.Login/Profile.Information.FieldError.PasswordMatch',
		...sharedProps,
	},
	CPPasswordResetFormLabelPasswordConfirm: {
		defaultValue: 'Confirm Password',
		description: 'Label for the password confirmation field.',
		displayPath: `${dpRoot}|Password Confirm Label`,
		displayType: 'Label',
		legacyName: 'CandidateAccess.Login/Profile.Information.FieldLabel.PasswordConfirm',
		...sharedProps,
	},
	CPPasswordResetFormErrorPasswordConfirmRequired: {
		defaultValue: 'Password confirmation is required',
		description: 'Error message when password confirmation is not provided.',
		displayPath: `${dpRoot}|Password Confirm Required Error`,
		displayType: 'Error',
		legacyName: 'CandidateAccess.Login/Profile.Information.FieldError.PasswordConfirmRequired',
		...sharedProps,
	},
	CPPasswordResetFormButtonResetPassword: {
		defaultValue: 'Reset Password',
		description: 'Submit button label for the password reset form.',
		displayPath: `${dpRoot}|Reset Password Button`,
		displayType: 'Button',
		legacyName: 'CandidateAccess.Login/Profile.Information.Button.ResetPassword',
		...sharedProps,
	},
	CPPasswordResetFormNotificationProblem: {
		defaultValue: 'There was a problem',
		description: 'Notification when there is a problem loading the form.',
		displayPath: `${dpRoot}|Problem Notification`,
		displayType: 'Notification',
		legacyName: 'CandidateAccess.Login/Profile.Information.Notification.Problem',
		...sharedProps,
	},
	CPPasswordResetFormNotificationPasswordUpdated: {
		defaultValue: 'Password updated',
		description: 'Notification when password has been successfully updated.',
		displayPath: `${dpRoot}|Password Updated Notification`,
		displayType: 'Notification',
		legacyName: 'CandidateAccess.Login/Profile.Information.Notification.PasswordUpdated',
		...sharedProps,
	},
};

export const LOC_KEYS = MapLocKeys<PasswordResetFormLoc>(cpPasswordResetFormLocs);
export type PasswordResetFormLocType = LocMap<PasswordResetFormLoc>;
